import { memo, useEffect, useRef, useState } from 'react';
import { ButtonPlacements, Carousel } from '@picsart/design-system/components/Carousel';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import useWindowSize from 'hooks/useWindowSize';
import { useDeviceType } from '@picsart/design-system/components/hooks';
import ITemplatesCarousel from './types';
import Template from '../Template';
import getPairItems from '../utils/pairItems';
import useStyles from './styles';

const TemplatesCarousel = ({ templates, scrollCount = 1 }: ITemplatesCarousel) => {
  const classes = useStyles();
  const isMobileDevice = useDeviceType();
  const { width } = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);
  const [intersectedTemplates, setIntersectedTemplates] = useState([]);
  const [scrollToRight, setScrollToRight] = useState(true);
  const [rightScrollAmount, setRightScrollAmount] = useState(0);
  const [leftScrollAmount, setLeftScrollAmount] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);

  const templateRefs = useRef([]);

  useEffect(() => {
    if (width < 425) {
      setIsMobile(true);
      return;
    }
    setIsMobile(false);
  }, [width]);

  const itemsToRender = isMobile ? getPairItems(templates) : templates;

  const calculateNextScrollAmount = index => {
    if (index >= templateRefs.current.length) {
      return templateRefs.current[index];
    }

    let totalWidth = 0;
    for (let i = 1; i <= scrollCount; i++) {
      if (index + i < templates.length) {
        totalWidth += templateRefs.current[index + i]?.offsetWidth;
      }
    }

    totalWidth += scrollCount * 16;

    return totalWidth;
  };

  const calculatePrevScrollAmount = index => {
    let totalWidth = 0;
    for (let i = 1; i <= scrollCount; i++) {
      if (index - i >= 0) {
        totalWidth += templateRefs.current[index - i]?.offsetWidth;
      }
    }

    totalWidth += (scrollCount + 1) * 16;

    return totalWidth;
  };

  useEffect(() => {
    const updateTemplates = entries => {
      entries.forEach(entry => {
        const { target } = entry;
        const dataIndex = Number(target.getAttribute('data-index'));

        setIntersectedTemplates(prevTemplates => {
          if (entry.isIntersecting) {
            return prevTemplates.includes(dataIndex)
              ? prevTemplates
              : [...prevTemplates, dataIndex];
          }
          return prevTemplates.filter(index => index !== dataIndex);
        });
      });
    };

    leftArrowRef.current = document.querySelector('.left');
    rightArrowRef.current = document.querySelector('.right');

    if (leftArrowRef.current) {
      leftArrowRef.current.addEventListener('mouseenter', () => setScrollToRight(false));
    }
    if (rightArrowRef.current) {
      rightArrowRef.current.addEventListener('mouseenter', () => setScrollToRight(true));
    }

    const observer = new IntersectionObserver(updateTemplates, {
      root: null,
      threshold: 1,
    });

    if (templateRefs.current && templateRefs.current.length > 0) {
      templateRefs.current.forEach(ref => {
        if (ref instanceof Element) observer.observe(ref);
      });
    }

    return () => {
      if (leftArrowRef.current) {
        leftArrowRef.current.removeEventListener('mouseenter', () => setScrollToRight(false));
      }
      if (rightArrowRef.current) {
        rightArrowRef.current.removeEventListener('mouseenter', () => setScrollToRight(true));
      }
      observer.disconnect();
    };
  }, [templates]);

  useEffect(() => {
    if (intersectedTemplates.length) {
      setStartIndex(Math.min(...intersectedTemplates));
      setEndIndex(Math.max(...intersectedTemplates));
    }
    const nextScrollAmount = calculateNextScrollAmount(endIndex);
    setRightScrollAmount(nextScrollAmount);
  }, [intersectedTemplates]);

  const handleNext = () => {
    setScrollToRight(true);
    const nextScrollAmount = calculateNextScrollAmount(endIndex);
    setRightScrollAmount(nextScrollAmount);
  };

  const handlePrev = () => {
    setScrollToRight(false);
    const prevScrollAmount = calculatePrevScrollAmount(startIndex);
    setLeftScrollAmount(prevScrollAmount);
  };

  return (
    <Carousel
      isMultiSlide
      hideArrows={isMobileDevice}
      dataTestId="templates-carousel"
      gap={Spacings.s16}
      buttonPlacement={ButtonPlacements.Overhang}
      onNext={handleNext}
      onPrevious={handlePrev}
      scrollAmount={scrollToRight ? rightScrollAmount : leftScrollAmount}
      ref={containerRef}
    >
      {itemsToRender.map((items, index) => {
        const templateItems = Array.isArray(items) ? items : [items];

        return (
          <div
            key={`${items[index]}-${index}`}
            className={classes.itemWrapper}
            data-pulse-section="template"
            data-index={index}
            ref={element => {
              templateRefs.current[index] = element;
            }}
          >
            {templateItems.map(subItem => {
              const { id, _id } = subItem;
              const templateId = id || _id;

              return (
                <Template
                  key={templateId}
                  template={subItem}
                  data-testid={templateId}
                  index={index}
                />
              );
            })}
          </div>
        );
      })}
    </Carousel>
  );
};

export default memo(TemplatesCarousel);
